import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@cian/frontend-reviews-components';
import { IconDislike16, IconLike16 } from '@cian/ui-kit';

import { Footer } from '../../../components/Footer';
import { getAnswerById } from '../../../selectors/answers';
import { EStatus } from '../../../types/likes';
import { setAnswerReaction } from '../../../actions/answers';
import { getIsAuthenticated } from '../../../selectors/user';
import { requestAuthentication } from '../../../actions/authentication';
import { NewAnswerButtonContainer } from '../../NewAnswerButton';
import { getIsReviewTab } from '../../../selectors/tabs';
import { setQuestionAnswerReaction } from '../../../actions/questionAnswers';
import { getReviewsConfig } from '../../../utils/getReviewsConfig/getReviewsConfig';
import { TThunkDispatch } from '../../../types/redux';

interface IFooterContainerProps {
  answerId: string;
}

export function FooterContainer({ answerId }: IFooterContainerProps) {
  const dispatch = useDispatch<TThunkDispatch>();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isReview = useSelector(getIsReviewTab);
  const answer = useSelector(getAnswerById(answerId));
  const reviewsConfig = getReviewsConfig();
  const showAnswerButton = reviewsConfig.CAN_USER_ANSWER_ON_ANSWER;

  const setReactionFn = isReview ? setAnswerReaction : setQuestionAnswerReaction;

  const setAnswerLike = React.useCallback(
    () => setReactionFn({ answerId, type: EStatus.Liked }),
    [answerId, setReactionFn],
  );
  const setAnswerDisliked = React.useCallback(
    () => setReactionFn({ answerId, type: EStatus.Disliked }),
    [answerId, setReactionFn],
  );

  const onLikeClick = React.useCallback(() => {
    if (isAuthenticated) {
      dispatch(setAnswerLike());
    } else {
      dispatch(requestAuthentication({ onSuccess: setAnswerLike }));
    }
  }, [dispatch, isAuthenticated, setAnswerLike, requestAuthentication]);

  const onDislikedClick = React.useCallback(() => {
    if (isAuthenticated) {
      dispatch(setAnswerDisliked());
    } else {
      dispatch(requestAuthentication({ onSuccess: setAnswerDisliked }));
    }
  }, [dispatch, isAuthenticated, setAnswerDisliked, requestAuthentication]);

  const answerJsx = showAnswerButton ? <NewAnswerButtonContainer type="answer" targetId={answerId} /> : null;

  const showRateButtons = reviewsConfig.SHOW_LIKE_BUTTONS;

  const likeJsx = showRateButtons ? (
    <IconButton
      Icon={IconLike16}
      active={answer.like.status === EStatus.Liked}
      onClick={onLikeClick}
      label={answer.like.likesCount}
    />
  ) : null;

  const dislikeJsx = showRateButtons ? (
    <IconButton
      Icon={IconDislike16}
      active={answer.like.status === EStatus.Disliked}
      onClick={onDislikedClick}
      label={answer.like.dislikesCount}
    />
  ) : null;

  return <Footer like={likeJsx} dislike={dislikeJsx} answer={answerJsx} />;
}
